import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { IResponse } from 'src/app/shared/models/app';
import { CacheResolverService } from '../cache/cache-resolver.service';
import { CacheConstant } from '../cache/cache.constant';
import { SessionStorageService } from 'ngx-webstorage';
import { BaseService } from './base.service';
import { ConfigApi } from '../constants/api.constant';
import { AppConstant, AppData, ProductTypes } from '../constants/app.constant';
import { Subject } from 'rxjs';
import { StaticService } from './static.service';
import { AlertService } from 'src/app/modules/alerts/alert.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService extends BaseService {
  /*********************Properties*********************/
  params = {};
  accountDetails: any = {};
  loggedInUserDetails: any = {};
  loggedInCustomerAccountDetails: any = {};

  orderDataLoaded$: Subject<any> = new Subject<any>();

  /*********************Properties*********************/

  /*********************Constructor*********************/
  constructor(
    protected http: HttpClient,
    private cache: CacheResolverService,
    private storage: SessionStorageService,
    private staticService: StaticService,
    private alertService: AlertService) {
    super(http);
  }

  get(shipTo = null) {

    const url = super.serviceUrl(this.apiConfig.config, ConfigApi.AccountDetails);
    const detail = this.cache.get(CacheConstant.LocalStorage.UserDetails);
    const redirectShipTO = detail.defaultShipToNumber ? detail.defaultShipToNumber : detail.showLoginShipToNumber;
    const shipToDetails = shipTo ? detail.customerAccountsList.find((x: any) => x.customerShipToNum === shipTo) :
      detail.customerAccountsList.find((x: any) => x.customerShipToNum === redirectShipTO);
   
    const req = {
      customerName: shipToDetails?.customerName,
      customerShipToNum: shipToDetails?.customerShipToNum,
      isCustomerMigratedToNewWO: shipToDetails?.isCustomerMigratedToNewWO,
      locationNum: shipToDetails?.locationNum,
      shipToOkFlag: shipToDetails?.shipToOkFlag,
      timeZoneNum: shipToDetails?.timeZoneNum,
      userNum: detail?.userDetailNum,
      userRole: detail?.userRoleXrefList?.find((x: any) => x.appTypeCode === 1).roleNum
    }
    return super.post(url, req).pipe(
      map((response: any) => {
        this.orderDetailsLoaded(response, detail, shipToDetails, req);
        return response.data;
      }));
  }

  private orderDetailsLoaded(response: any, detail: any, shipToDetails: any, req: any) {

    if (!response.hasError) {
      this.cache.set(CacheConstant.LocalStorage.AccountDetails, response.data);
      const patientBenefitEligibleProducts =this.getpatientBenefitEligibleProducts(response.data);

      if (detail) {
        detail.showLoginShipToNumber = shipToDetails.customerShipToNum;

        this.cache.set(CacheConstant.LocalStorage.UserDetails, detail)
      }
      this.cache.set(CacheConstant.LocalStorage.isCustomerMigratedToNewWO, shipToDetails.isCustomerMigratedToNewWO);
      this.cache.set(CacheConstant.LocalStorage.ShipTo, req.customerShipToNum);
      this.cache.set(CacheConstant.LocalStorage.LocationNum, req.locationNum);
      this.cache.set(CacheConstant.LocalStorage.AlertCount, response.data.alertCount);
      this.alertService.alertCount$.next(response.data.alertCount)
      this.cache.set(CacheConstant.LocalStorage.UserNum, req.userNum);
      this.cache.set(CacheConstant.LocalStorage.CustomerAdminSettings, response.data.customerAdminSettings);
      this.cache.set(CacheConstant.LocalStorage.Procedures, response.data.procedures);
      this.filterSupplies(response.data.customerCatalog)
      this.cache.set(CacheConstant.LocalStorage.Products, response.data.customerCatalog);
      this.cache.set(CacheConstant.LocalStorage.RecurringOrders, response.data.procedures);
      this.cache.set(CacheConstant.LocalStorage.Components, response.data.components);
      this.cache.set(CacheConstant.LocalStorage.patientBenefitEligibleProducts, patientBenefitEligibleProducts);
      let refFPhy: any = [];
      response.data.referringPhysicians.forEach((obj: any) => {
        obj.fullName = obj.lastName + ', ' + obj.firstName;
        if (obj.activeFlag) {
          refFPhy.push(obj);
        }
      });
      this.cache.set(CacheConstant.LocalStorage.ReferringPhysicians, refFPhy);
      response.data.orderingPhysicians.forEach((obj: any) => {
        obj.fullName = obj.lastName + ', ' + obj.firstName;

      });
      this.cache.set(CacheConstant.LocalStorage.OrderingPhysicians, response.data.orderingPhysicians);
      this.cache.set(CacheConstant.LocalStorage.PurchaseOrders, response.data.purchaseOrders);
      this.cache.set(CacheConstant.LocalStorage.RecurringOrderTemplateExists, response.data.recurringOrderTemplateExists);

      this.cache.set(CacheConstant.LocalStorage.OrderDataLoaded,
        {
          'isOrderDataLoaded': true,
          'orderDataLoadedMsg': ""
        });
    }
    else {
      this.cache.set(CacheConstant.LocalStorage.OrderDataLoaded,
        {
          'isOrderDataLoaded': false,
          'orderDataLoadedMsg': response.error.faultInfos[0].faultMessage
        });
    } 

    this.orderDataLoaded$.next(response);    

  }

  filterSupplies(supplies: any) {
    const supplyList = supplies.filter((obj: any) => obj.productTypeNum != ProductTypes.DRG);

    let suppL: any = [];
    supplyList.forEach((obj: any) => {
      let ss = suppL.filter((obj1: any) => obj1.productConfigNum == obj.productConfigNum);
      if (ss.length == 0 && obj.productTypeNum) {
        suppL.push(obj);
      }
    });

    this.cache.set(CacheConstant.LocalStorage.Supplies, suppL);

  }

  updateDefaultAccount(userDetail: any) {
    const url = super.serviceUrl(this.apiConfig.user, ConfigApi.DefaultAccount + ConfigApi.Save);
    return super.post(url, userDetail);
  }

  generateOktaToken() {
    const url = super.serviceUrl(this.apiConfig.user, ConfigApi.OktaB2B); 

    return super.post(url, {},
      {

        observe: 'response'
      }
    ).pipe(
      map((response: any) => {
        if (!response.hasError && response.data) {
          return response.data;
        } else {
          return null;
        }
      })
    );

  }

  setParams(shipToDetails: any, detail: any) {
    this.params = {
      customerName: shipToDetails?.customerName,
      customerShipToNum: shipToDetails?.customerShipToNum,
      isCustomerMigratedToNewWO: shipToDetails?.isCustomerMigratedToNewWO,
      locationNum: shipToDetails?.locationNum,
      shipToOkFlag: shipToDetails?.shipToOkFlag,
      timeZoneNum: shipToDetails?.timeZoneNum,
      userNum: detail?.userDetailNum,
      userRole: detail?.userRoleXrefList?.find((x: any) => x.appTypeCode === 1).roleNum
    }
  } 

  userAccountChange(userDetail: any) {

    const url = this.apiConfig.user + ConfigApi.DefaultAccSave; 

    return super.post(url, userDetail,
      {

        observe: 'response'
      }
    ).pipe(
      map((response: any) => {
        if (!response.hasError && response.data) {
          return response.data;
        } else {
          return null;
        }
      })
    );


  }

  getTimeZone() {
    return this.storage.retrieve(CacheConstant.SessionStorageData.TimeZoneText);
  } 

  initializeAccountService() {
    var customerDefaultAccount = this.getCustomerDefaultAccount();

    if (customerDefaultAccount) {
      this.params = {
        customerName: customerDefaultAccount.customerName,
        customerShipToNum: customerDefaultAccount.customerShipToNum,
        locationNum: customerDefaultAccount.locationNum,
        timeZoneNum: customerDefaultAccount.timeZoneNum,
        shipToOkFlag: customerDefaultAccount.shipToOkFlag,
        isCustomerMigratedToNewWO: customerDefaultAccount.isCustomerMigratedToNewWO,
        userNum: this.loggedInUserDetails.userDetailNum
      };
    }


  }

  getCustomerDefaultAccount() {
    this.accountDetails = this.cache.get(CacheConstant.LocalStorage.AccountDetails);
    this.loggedInUserDetails = this.cache.get(CacheConstant.LocalStorage.UserDetails);
    return this.loggedInUserDetails.customerAccountsList
      .filter((x: any) => x.customerShipToNum === this.loggedInUserDetails.showLoginShipToNumber)
      .shift();

  }

  getAccountDetailsCacheItem(itemName: any) {
    let accountDetails = this.cache.get(CacheConstant.LocalStorage.AccountDetails);
    return accountDetails ? accountDetails[itemName] : null;
  }

  getSyntracUrl(authTokenString: any) {
    var url = '';
    var queryParams = [];
    var syntracUrlPreference = this.staticService.getApplicationPreference(AppConstant.Preferences.DMS_APP_URL);

    if (syntracUrlPreference) {
      url = syntracUrlPreference.preferenceValue + "/#/";
      let authToken = authTokenString ? authTokenString : this.storage.retrieve(CacheConstant.SessionStorageData.AuthToken);
      queryParams.push({ key: 'AUTH_TOKEN', value: authToken });

      var oktaAccessToken = this.storage.retrieve(CacheConstant.SessionStorageData.AccessToken);
      queryParams.push({ key: 'ACCESS_TOKEN', value: oktaAccessToken });

      var oktaIdToken = this.storage.retrieve(CacheConstant.SessionStorageData.IdToken);
      queryParams.push({ key: 'ID_TOKEN', value: oktaIdToken });

      let refreshToken = this.storage.retrieve(CacheConstant.SessionStorageData.RefreshToken);
      queryParams.push({ key: 'REFRESH_TOKEN', value: refreshToken });

      url = this.setQueryStringParams(url, queryParams);
    }
    this.cache.clear();
    this.storage.clear();
    return url;
  }

  setQueryStringParams(url: any, nameValueCollection: any) {
    for (var param in nameValueCollection) {
      var queryString = nameValueCollection[param];
      var separator = url.indexOf('?') !== -1 ? "&" : "?";
      url = url + separator + queryString.key + "=" + queryString.value;
    }

    return url;
  }

  getpatientBenefitEligibleProducts(accountDetails: any){
    const productBenifitList = accountDetails.customerCatalog
      .filter((obj: any) => (obj.productTypeNum == ProductTypes.DRG && obj.isEnablePatientBenefitEligibilityFlag === true))
      .map((val: any) => val.productSaleUseNum);
      if (Array.isArray(productBenifitList) && productBenifitList.length > 0){
        return productBenifitList;
      }
      return [];    
  }
}
